import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { LogoutAction, RefreshTokenAction } from '@tsin-core/actions/auth.action';
import { AuthState } from '@tsin-core/states/auth.state';
import { ActivityTrackerService } from '@tsin-core/services/common/activity-tracker.service';
import { environment } from '@tsin-env/environment';
import { catchError, switchMap, tap, throwError } from 'rxjs';

export const jwtInterceptor: HttpInterceptorFn = (req, next) => {

  const store: Store = inject(Store);
  const activityTracker: ActivityTrackerService = inject(ActivityTrackerService);
  const token = store.selectSnapshot<string | null>(AuthState.getToken);
  const refreshToken = store.selectSnapshot<string | null>(AuthState.getRefreshToken);

  if (token && token.length > 0) {
    if (!req.url.includes('/api/auth/refresh')) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }
  }

  return next(req).pipe(
    tap(() => {
      // Record activity on successful server request
      activityTracker.recordActivity();
    }),
    catchError((error: any) => {
      if (error.status === 401 &&
        error.error &&
        refreshToken &&
        !req.url.includes('/api/auth/refresh')) {

        // Dispatch refresh token action and wait for new token
        return store.dispatch(new RefreshTokenAction({ refreshToken })).pipe(
          catchError(() => throwError(() => error)), // If refresh fails, propagate original error
          switchMap(() => {
            // Get the new token from the store after refresh
            const newToken = store.selectSnapshot<string | null>(AuthState.getToken);

            if (newToken) {
              // Clone the original request with the new token
              const retryReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`
                }
              });

              // Retry the original request with the new token
              return next(retryReq);
            }

            return throwError(() => error);
          })
        );
      }

      if (error.status === 403 &&
        error.error &&
        error.error.error === 'Forbidden') {

        // Dispatch logout action
        store.dispatch(new LogoutAction());

        // Redirect to user management URL
        window.location.href = environment.userMgtUrl;
      }

      // For other errors, just propagate
      return throwError(() => error);
    })
  );
};
